/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CopyIcon from '@atlaskit/icon/core/copy';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { messages } from './messages.ts';

const styles = cssMap({
	copyButton: {
		width: '32px',
		height: '32px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		},
		borderRadius: token('radius.small', '3px'),
	},

	fullSizeText: { maxHeight: '100%', overflowY: 'auto' },

	container: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.input'),
		borderRadius: token('radius.small'),
		whiteSpace: 'pre-line',
		position: 'relative',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		flex: 1,
		minHeight: '50px',
	},

	copyContainer: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},

	smallText: {
		maxHeight: '400px',
		overflowY: 'scroll',
	},

	hidden: {
		opacity: 0,
	},
});

type AgentTextareaFieldProps = {
	value?: string | null;
	noValuePlaceholder: string;
	onCopy: (text: string) => void;
	fullHeightText?: boolean;
};

export const AgentTextareaField = ({
	value,
	noValuePlaceholder,
	onCopy,
	fullHeightText = true,
}: AgentTextareaFieldProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const [isCopied, setIsCopied] = useState(false);

	const tooltipLabel = isCopied ? messages.copiedTooltip : messages.copyTooltip;

	return (
		<Box
			xcss={cx(styles.container, fullHeightText ? styles.fullSizeText : styles.smallText)}
			onMouseEnter={() => {
				setIsHovered(true);
			}}
			onMouseLeave={() => {
				setIsHovered(false);
				setIsCopied(false);
			}}
		>
			{value ? (
				<>
					<Text>{value}</Text>
					<Box xcss={cx(styles.copyContainer, !isHovered && styles.hidden)}>
						<Tooltip content={formatMessage(tooltipLabel)} position="top">
							<Pressable
								aria-label={formatMessage(tooltipLabel)}
								xcss={styles.copyButton}
								onBlur={() => setIsHovered(false)}
								onFocus={() => setIsHovered(true)}
								onClick={() => {
									onCopy(value);
									setIsCopied(true);
								}}
							>
								{isCopied ? (
									<CheckMarkIcon color={token('color.icon')} label="" />
								) : (
									<CopyIcon color={token('color.icon')} label="" />
								)}
							</Pressable>
						</Tooltip>
					</Box>
				</>
			) : (
				<Text>{noValuePlaceholder}</Text>
			)}
		</Box>
	);
};
